import { normalizeFa } from 'utilities/helper/fundOrder'

type ISearchableAccount = Pick<
    IBankAccountCards,
    'full_name' | 'owner_name' | 'bank_name' | 'card_number' | 'iban'
>

/**
 * Does this account answer the manager's search in the payout picker?
 *
 * A busy manager's picker lists every account the box has paid to, and
 * scrolling forty near-identical rows for one name was the complaint. They
 * search the way they remember an account: by the holder's name («مریم»,
 * typed on whatever keyboard — Arabic yeh/kaf fold the same as Persian), by
 * the bank, or by a run of digits from the card or the IBAN («۶۰۳۷», «4321»).
 *
 * Name tokens must all appear, in any order. A query that is only digits
 * (spaces, dashes and a leading «IR» ignored) is matched against the card
 * number and the IBAN instead, so «۶۰۳۷ ۹۹» finds 6037-99… however it is
 * grouped on screen.
 */
export function matchesAccount(
    account: ISearchableAccount,
    query: string
): boolean {
    const normalized = normalizeFa(query)
    if (!normalized) return true

    const digits = normalized.replace(/[\s-]/g, '').replace(/^ir/, '')
    if (/^\d+$/.test(digits)) {
        const numbers = [account.card_number, account.iban]
            .filter(Boolean)
            .map((value) => normalizeFa(value).replace(/[\s-]/g, ''))
        return numbers.some((value) => value.includes(digits))
    }

    const haystack = normalizeFa(
        [account.full_name, account.owner_name, account.bank_name]
            .filter(Boolean)
            .join(' ')
    )
    return normalized
        .split(' ')
        .filter(Boolean)
        .every((token) => haystack.includes(token))
}

/** The search box only earns its space once there is something to scroll. */
export const SEARCH_THRESHOLD = 4
